"use client";

import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import {
	BLACK_FRIDAY_CAMPAIGN_CONFIG,
	BLACK_FRIDAY_ANNOUNCEMENT_START,
} from "@/lib/campaigns/black-friday/config";
import { getCampaignPhase, getCountdownTarget, type CampaignPhase } from "@/lib/campaigns/black-friday/phase";
import { isCampaignAnnounced } from "@/lib/campaigns/black-friday/surfaces";

const currentPhase = () => getCampaignPhase(new Date(), BLACK_FRIDAY_CAMPAIGN_CONFIG);
const currentAnnouncement = () => isCampaignAnnounced(new Date());

function subscribe(onChange: () => void) {
	let timer: ReturnType<typeof setTimeout>;
	function schedule() {
		const target = currentAnnouncement()
			? getCountdownTarget(currentPhase(), BLACK_FRIDAY_CAMPAIGN_CONFIG)
			: new Date(BLACK_FRIDAY_ANNOUNCEMENT_START);
		if (!target) return;
		timer = setTimeout(
			() => {
				onChange();
				schedule();
			},
			Math.min(2_147_483_647, Math.max(1, target.getTime() - Date.now() + 1)),
		);
	}
	const onVisible = () => {
		clearTimeout(timer);
		onChange();
		schedule();
	};
	schedule();
	document.addEventListener("visibilitychange", onVisible);
	return () => {
		clearTimeout(timer);
		document.removeEventListener("visibilitychange", onVisible);
	};
}

/** Shared layouts survive navigation. Hide stale claims immediately, then obtain a fresh server phase. */
export function CampaignBoundary({
	phase,
	announced,
	children,
}: {
	phase: CampaignPhase;
	announced?: boolean;
	children: ReactNode;
}) {
	const router = useRouter();
	const clientPhase = useSyncExternalStore(subscribe, currentPhase, () => phase);
	const clientAnnouncement = useSyncExternalStore(subscribe, currentAnnouncement, () => announced ?? true);
	useEffect(() => {
		if (clientPhase !== phase || (announced !== undefined && clientAnnouncement !== announced))
			router.refresh();
	}, [clientPhase, phase, clientAnnouncement, announced, router]);
	return clientPhase === phase && (announced === undefined || (announced && clientAnnouncement))
		? children
		: null;
}
